import { useEffect, useState } from "react";
import { Contact2Icon, FilePenLine, QrCode, ScanQrCode, ShieldCheck, TreePalm, Users2 } from "lucide-react";
import { NavLink } from "react-router-dom";
import { Attendance, ConfirmationStatus, EVENT_ID_SLA2026, ResponseCode, Roles } from "iasma-datatypes";
import { useAuth } from "@/contexts/AuthContext";
import { getConfirmation, setConfirmation } from "@/services/EventService";
import { useNotify } from "@/contexts/NotifyContext/useNotify";
import axios from "axios";
import { NavBack } from "@/components/layout/Menu/NavBack";

function useGoogleFonts() {
	useEffect(() => {
		const id = "undangan-google-fonts";
		if (document.getElementById(id)) return;
		const link = document.createElement("link");
		link.id = id;
		link.rel = "stylesheet";
		link.href =
			"https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,400;0,9..144,600;1,9..144,500;1,9..144,600&family=Karla:wght@400;500;700&display=swap";
		document.head.appendChild(link);
	}, []);
}


const CHOICES: ConfirmationStatus[] = [1, 2, 3];

const CHOICE_LABEL: Record<ConfirmationStatus, string> = {
	0: "Belum Konfirmasi",
	1: "InsyaAllah Hadir",
	2: "Mungkin Hadir",
	3: "Tidak Hadir",
};

const CHOICE_RESPONSE: Record<ConfirmationStatus, string> = {
	0: "belum konfirmasi",
	1: "siap hadir dan tidak sabar bersua",
	2: "akan berusaha untuk hadir",
	3: "belum bisa hadir kali ini",
};

function DetailRow({ icon, label, value, sub }: {
	icon: React.ReactNode;
	label: string;
	value: string;
	sub?: string;
}) {
	return (
		<div className="flex items-start gap-4 rounded-[10px] border border-[#7B1E28]/10 bg-[#FBF5EA] p-4">
			<div className="flex h-9.5 w-9.5 flex-none items-center justify-center rounded-full bg-[#7B1E28] text-[#E8CD8C]">
				{icon}
			</div>
			<div>
				<div className="mb-0.5 text-[0.72rem] uppercase tracking-[0.14em] text-[#7B1E28]/80">
					{label}
				</div>
				<div className="font-['Fraunces',serif] text-[1.08rem] font-medium text-[#2A1810]">
					{value}
				</div>
				{sub && <div className="mt-0.5 text-[0.92rem] text-[#2A1810]">{sub}</div>}
			</div>
		</div>
	);
}

function InformationLink({ icon, path, label, value, sub }: {
	icon: React.ReactNode;
	path: string;
	label: string;
	value: string;
	sub?: string;
}) {
	return (
		<NavLink to={path} className="flex items-center gap-4 rounded-[10px] border border-[#7B1E28]/10 bg-[#FBF5EA] p-4">
			<div className="flex h-12 w-12 flex-none items-center justify-center">
				{icon}
			</div>
			<div>
				<div className="mb-0.5 text-[0.72rem] uppercase tracking-[0.14em] text-[#7B1E28]/80">
					{label}
				</div>
				<div className="font-['Fraunces',serif] text-[1.08rem] font-medium text-[#2A1810]">
					{value}
				</div>
				{sub && <div className="mt-0.5 text-[0.92rem] text-[#2A1810]">{sub}</div>}
			</div>
		</NavLink>
	);
}

function SocialLink({
	href,
	label,
	children,
}: {
	href: string;
	label: string;
	children: React.ReactNode;
}) {
	return (
		<a
			href={href}
			aria-label={label}
			target="_blank"
			title={label}
			className="flex h-10.5 w-10.5 items-center justify-center rounded-full border border-[#E8CD8C]/40 text-[#E8CD8C] transition-colors hover:bg-[#E8CD8C]/15"
		>
			{children}
		</a>
	);
}

export function SLA2026() {
	const { session, setToken, hasRole } = useAuth();
	const { notify, setNotify } = useNotify();
	const [attendance, setAttendance] = useState<Attendance | undefined>(undefined);

	const [confirmationStatus, setConfirmationStatus] = useState<ConfirmationStatus>(0);
	const [submitted, setSubmitted] = useState<{ choice: ConfirmationStatus } | null>(null);

	useGoogleFonts();

	useEffect(() => {
		if (session?.User?.Alumni) {
			setNotify(null);
			setToken(axios);
			getConfirmation(EVENT_ID_SLA2026).then((data) => {
				console.log("Confirmation data:", data);
				if (data.message) {
					setNotify({ title: "Get Confirmation Error", error: data, severity: 'error' });
				} else {
					setAttendance(data);
					setConfirmationStatus(data.Confirmation);
				}
			}).catch((error) => {
				console.error("Error fetching confirmation:", error);
				setNotify({ title: "Get Confirmation Error", error, severity: 'error' });
			});
		}
	}, [session?.User?.Alumni]);

	useEffect(() => {
		if (confirmationStatus && submitted) {
			const newAttendance: Attendance = {
				EventId: EVENT_ID_SLA2026,
				Confirmation: confirmationStatus,
				AlumniId: 0,
				Status: ResponseCode.Unknown
			};

			setNotify(null);
			setToken(axios);
			setConfirmation(newAttendance).then((data) => {
				setAttendance(newAttendance);
			}).catch((error) => {
				console.error("Error updating confirmation:", error);
				setNotify({ title: "Set Confirmation Error", error, severity: 'error' });
			});
			setSubmitted(null);
		}
	}, [confirmationStatus, submitted]);

	const handleSubmit = () => {
		if (!confirmationStatus) return;
		setSubmitted({ choice: confirmationStatus });
	};



	if (session?.User?.Alumni) {
		return (
			<div className="bg-[#FBF5EA] text-[#2A1810] font-['Karla',sans-serif]">
				<NavBack />
				{/* HERO */}
				<section className="bg-[#7B1E28] px-6 pt-5 text-center text-[#FBF5EA]">
					<p className="mx-auto max-w-195 px-1 font-['Fraunces',serif] text-[clamp(1.4rem,4.2vw,2.5rem)] italic leading-relaxed">
						<span className="mr-1 align-[-0.32em] font-['Fraunces',serif] text-[2.6em] not-italic font-semibold leading-none text-[#C79A3C]">&ldquo;</span>
						Biapun bajarak, tetap badunsanak. Ka Banduang malapeh taragak.
						<span className="ml-1 align-[-0.32em] font-['Fraunces',serif] text-[2.6em] not-italic font-semibold leading-none text-[#C79A3C]">&rdquo;</span>
					</p>
					{/* <p className="mt-6 text-[0.95rem] tracking-wide text-[#E8CD8C]">
					Walau berjarak, tetap bersaudara &mdash; ke Bandung melepas rindu
				</p> */}
					{/* <div className="mt-12">
					<GonjongDivider fill="#F5EADA" />
				</div> */}
				</section>

				{/* EVENT */}
				<section className="bg-[#F5EADA] px-6 pb-12 pt-14 text-center">
					<div className="mb-3 text-[0.78rem] uppercase tracking-[0.28em] text-[#7B1E28]/75">
						Assalamualaikum Warahmatullahi Wabarakatuh
					</div>
					<h1
						className="mx-auto mb-3 max-w-160 border-b border-dashed border-[#C79A3C] pb-1 font-['Fraunces',serif] text-[clamp(1.7rem,4.5vw,2.1rem)] font-semibold text-[#3F0F16]"
						title="Ganti dengan nama acara sebenarnya"
					>
						Silahturahmi Lintas Angkatan (SLA) &mdash; IASMA 1 Landbouw Bukittinggi
					</h1>
					<p
						className="mx-auto mb-10 max-w-140 border-b border-dashed border-[#C79A3C] pb-1 text-[0.98rem] text-[#5a4838]"
						title="Ganti dengan deskripsi singkat acara"
					>
						Uni, Uda dan Dunsanak sadonyo, mari basamo-samo maliek muko, maulang
						carito, sarato mampaarek tali silaturahmi.
					</p>

					<div className="mx-auto grid max-w-130 gap-4 text-left">
						<DetailRow
							label="Hari & tanggal"
							value="Sabtu, 10 Oktober 2026"
							icon={
								<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} className="h-4.5 w-4.5">
									<rect x="3" y="5" width="18" height="16" rx="2" />
									<path d="M8 3v4M16 3v4M3 10h18" />
								</svg>
							}
						/>
						{/* <DetailRow
						label="Waktu"
						value="10.00 WIB — selesai"
						icon={
							<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} className="h-4.5 w-4.5">
								<circle cx="12" cy="12" r="9" />
								<path d="M12 7v5l3 3" />
							</svg>
						}
					/> */}
						<DetailRow
							label="Tempat"
							value="Bumi Sangkuriang"
							sub="Ciumbuleuit, Bandung, Jawa Barat"
							icon={
								<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} className="h-4.5 w-4.5" onClick={() => window.open("https://maps.app.goo.gl/AcBr3RQbB3VFaVSDA", "_blank")} cursor="pointer">
									<path d="M12 21s7-6.5 7-12a7 7 0 1 0-14 0c0 5.5 7 12 7 12z" />
									<circle cx="12" cy="9" r="2.3" />
								</svg>
							}
						/>
					</div>
				</section>

				{/* RSVP */}
				<section className="bg-[#3F0F16] px-6 py-14 text-center text-[#FBF5EA]">
					<h2 className="mb-2 font-['Fraunces',serif] text-[clamp(1.4rem,3.6vw,1.9rem)] font-semibold">
						Konfirmasi kehadiran
					</h2>
					<p className="mx-auto mb-8 max-w-115 text-[0.92rem] text-[#E8CD8C]">
						Bantu kami mempersiapkan acara dengan mengisi konfirmasi di bawah ini.
					</p>

					<div className="mx-auto max-w-105">
						<input
							type="text"
							readOnly
							value={session?.User?.Alumni?.Name + " (" + session?.User?.Alumni?.Graduation + ")" || ""}
							placeholder="Nama lengkap"
							className="mb-5 w-full rounded-lg border border-[#E8CD8C]/35 bg-white/6 px-4 py-3 text-[0.95rem] text-[#FBF5EA] placeholder:text-[#F5EADA]/50 focus:outline-none focus:ring-1 focus:ring-[#C79A3C]"
						/>

						<div className="mb-6 grid grid-cols-3 gap-2 sm:gap-3">
							{CHOICES.map((choice) => (
								<button
									key={choice}
									type="button"
									onClick={() => setConfirmationStatus(choice)}
									className={`rounded-lg border px-2 py-3 text-[0.8rem] font-bold transition-colors sm:text-[0.85rem] ${confirmationStatus === choice
										? "border-[#C79A3C] bg-[#C79A3C] text-[#3F0F16]"
										: "border-[#E8CD8C]/35 bg-transparent text-[#FBF5EA] hover:border-[#C79A3C]"
										}`}
								>
									{CHOICE_LABEL[choice]}
								</button>
							))}
						</div>

						<button
							type="button"
							onClick={handleSubmit}
							disabled={confirmationStatus === attendance?.Confirmation}
							className="w-full rounded-lg bg-[#C79A3C] py-3 text-[0.95rem] font-bold text-[#3F0F16] transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-45"
						>
							Kirim konfirmasi
						</button>

						<p className="mt-5 min-h-[1.4em] text-[0.92rem] text-[#E8CD8C]">
							{submitted &&
								`Terima kasih, ${session?.User?.Alumni?.Name || "Sanak"} — konfirmasi tercatat: ${CHOICE_RESPONSE[submitted.choice]}.`}
						</p>
					</div>
					{/* <p className="font-['Fraunces',serif] text-base italic text-[#E8CD8C]">
					Ditunggu kehadiran sanak sadonyo &mdash; sampai basuo di Banduang
				</p> */}
				</section>

				{/* Menu */}
				<section className="bg-[#F5EADA] px-6 pb-12 pt-14 text-center">
					<h2 className="mb-2 font-['Fraunces',serif] text-[clamp(1.4rem,3.6vw,1.9rem)] font-semibold">
						Tautan Lainnya
						<p className="mx-auto mb-8 max-w-115 text-[0.92rem] text-[#5a4838]">
							Berikut beberapa tautan yang bisa diakses terkait SLA 2026 dan IASMA.
						</p>
					</h2>
					<div className="mx-auto grid max-w-130 gap-4 text-left">
						
						<div className="text-lg font-semibold mt-4">Tautan menjelang hari H</div>
						
						<InformationLink
							label="Venue"
							path="/sla/venue"
							value="Lokasi Acara"
							sub="gambaran suasana lokasi acara"
							icon={<TreePalm size={48} />}
						/>
						<InformationLink
							label="Data Konfirmasi"
							path="/sla/chart-confirmation"
							value="Tabel Konfirmasi"
							sub="lihat data konfirmasi sadonyo"
							icon={<Users2 size={48} />}
						/>

						<div className="text-lg font-semibold mt-4">Tautan untuk hari H (10 Oktober 2026)</div>

						<InformationLink
							label="Absensi"
							path="/sla/showqr"
							value="Buat QR Kehadiran"
							sub="kode QR untuk kehadiran"
							icon={<QrCode size={48} />}
						/>
						{ hasRole(Roles.PANITIA_ABSENSI) &&
						<InformationLink
							label="Scan Absensi"
							path="/sla/scanqr"
							value="Pindai QR Kehadiran"
							sub="memindai kode QR kehadiran yang diperlihatan tamu"
							icon={<ScanQrCode size={48} />}
						/>}
						{ hasRole(Roles.PANITIA_ABSENSI) &&
						<InformationLink
							label="Absensi Manual"
							path="/sla/absensi"
							value="Manual Kehadiran"
							sub="mencatat kehadiran secara manual (tanpa QR)"
							icon={<FilePenLine size={48} />}
						/>}
						
						<InformationLink
							label="Data Absensi"
							path="/sla/chart-presence"
							value="Tabel Kehadiran"
							sub="lihat data kehadiran"
							icon={<Users2 size={48} />}
						/>

						<div className="text-lg font-semibold mt-4">Tautan lainnya</div>

						<InformationLink
							label="Biodata"
							path="/directory/personal"
							value="Ubah Biodata"
							sub="update biodata pada database IASMA"
							icon={<Contact2Icon size={48} />}
						/>
						{ hasRole(Roles.ADMIN_ALL) &&
						<InformationLink
							label="Roles"
							path="/directory/roles"
							value="Pengaturan Akses"
							sub="mengatur akses menu untuk panitia"
							icon={<ShieldCheck size={48} />}
						/>}
					</div>

				</section>
				{/* FOOTER */}
				<footer className="bg-[#7B1E28] px-6 pb-9 pt-11 text-center">
					<p className="mb-6 font-['Fraunces',serif] text-base italic text-[#E8CD8C]">
						Tautan media sosial IASMA & SLA 2026
					</p>
					<div className="mb-6 flex flex-wrap justify-center gap-3">
						{/* <SocialLink href="https://facebook.com" label="Facebook">
						<svg viewBox="0 0 24 24" fill="currentColor" className="h-4.75 w-4.75">
							<path d="M13.5 21v-7.5H16l.4-3H13.5V8.4c0-.87.24-1.46 1.5-1.46H16.5V4.35c-.26-.04-1.15-.11-2.19-.11-2.17 0-3.66 1.32-3.66 3.75v2.5H8v3h2.65V21h2.85z" />
						</svg>
					</SocialLink> */}
						<SocialLink href="https://www.instagram.com/panitiasla2026" label="Instagram">
							<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} className="h-4.75 w-4.75">
								<rect x="3" y="3" width="18" height="18" rx="5" />
								<circle cx="12" cy="12" r="4" />
								<circle cx="17.2" cy="6.8" r="1" />
							</svg>
						</SocialLink>
						<SocialLink href="https://www.tiktok.com/@panitia.sla.2026" label="TikTok">
							<svg viewBox="0 0 24 24" fill="currentColor" className="h-4.75 w-4.75">
								<path d="M14.5 3c.4 2.2 1.8 3.6 4 3.9v2.6c-1.5 0-2.8-.4-4-1.2v6.1c0 3.1-2.5 5.6-5.6 5.6S3.3 17.5 3.3 14.4 5.8 8.8 8.9 8.8c.3 0 .6 0 .9.1v2.7c-.3-.1-.6-.2-.9-.2-1.6 0-2.9 1.3-2.9 3s1.3 3 2.9 3 3-1.3 3-3V3h2.2z" />
							</svg>
						</SocialLink>
						<SocialLink href="https://wa.me/+6281312347108" label="WhatsApp">
							<svg viewBox="0 0 24 24" fill="currentColor" className="h-4.75 w-4.75">
								<path d="M12 3a9 9 0 0 0-7.7 13.6L3 21l4.5-1.3A9 9 0 1 0 12 3zm5.1 12.8c-.2.6-1.3 1.2-1.8 1.2-.5.1-1 .1-3.2-.8-2.7-1.1-4.4-3.8-4.5-4-.1-.2-1-1.4-1-2.6s.6-1.9.8-2.1c.2-.2.5-.3.7-.3h.5c.2 0 .4 0 .6.5.2.6.7 1.9.8 2 .1.2.1.4 0 .6-.1.2-.1.3-.3.5-.2.2-.3.4-.5.5-.2.2-.4.4-.2.7.2.3.9 1.5 2 2.4 1.3 1.2 2.4 1.5 2.8 1.7.3.2.5.1.7-.1.2-.2.8-.9 1-1.2.2-.3.4-.3.7-.2.3.1 1.7.8 2 .9.3.2.5.2.6.4.1.2.1.9-.1 1.5z" />
							</svg>
						</SocialLink>
						<SocialLink href="https://www.youtube.com/@iasmasatulandbouwbukittinggi?sub_confirmation=1" label="YouTube">
							<svg viewBox="0 0 24 24" fill="currentColor" className="h-4.75 w-4.75">
								<path d="M21.6 7.2c-.2-1-1-1.7-1.9-1.9C18 5 12 5 12 5s-6 0-7.7.3c-1 .2-1.7 1-1.9 1.9C2 8.9 2 12 2 12s0 3.1.4 4.8c.2 1 1 1.7 1.9 1.9C6 19 12 19 12 19s6 0 7.7-.3c1-.2 1.7-1 1.9-1.9.4-1.7.4-4.8.4-4.8s0-3.1-.4-4.8zM10 15V9l5.2 3-5.2 3z" />
							</svg>
						</SocialLink>
						<SocialLink href="https://twb.nz/2026sla" label="Twibbon">
							<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} className="h-4.75 w-4.75">
								<rect x="3.5" y="3.5" width="17" height="17" rx="4" />
								<circle cx="12" cy="12" r="4.5" />
							</svg>
						</SocialLink>
					</div>
					{/* <p className="text-[0.78rem] text-[#F5EADA]/55">
					Bagikan tautan ini kepada sanak dan dunsanak lainnya
				</p> */}
				</footer>
			</div>
		);
	} else {
		return (
			<div className="flex h-screen flex-col items-center justify-center gap-4 bg-[#FBF5EA] text-[#2A1810] font-['Karla',sans-serif]">
				<p className="text-lg font-semibold">Anda harus login untuk mengakses halaman ini.</p>
				<NavLink to="/" className="rounded-lg bg-[#C79A3C] px-4 py-2 text-[#3F0F16] transition-opacity hover:opacity-90">
					Kembali ke Beranda
				</NavLink>
			</div>
		);
	}

}	